import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";
import { CloudDownload, RefreshCw, CircleCheck, CircleAlert, ChevronDown, ChevronUp } from "lucide-react";
import { theme, card, btnPrimary, btnSecondary } from "../theme";
import { api, ApiError } from "../api/client";
import { StatusTag } from "./Drawing";
import { fmtDateTime } from "../lib/format";
import { QualityChip, type QualityReview } from "./EvidenceQualityReview";

// Import evidence from connected storage (Microsoft 365, OneDrive, SharePoint,
// Google Drive) into the Evidence Repository. Backed by
// GET /documents/import-status, POST /documents/import-connected and
// GET /documents/import-runs/:id — the work itself runs on the worker, so the
// panel polls the run until it completes.

export type EvidenceSourceKey = "m365" | "onedrive" | "sharepoint" | "google_drive";

export interface EvidenceSourceStatus {
  key: EvidenceSourceKey;
  label: string;
  connected: boolean;
  importable: boolean;
  note: string;
}

export interface ImportRun {
  id: string;
  status: "queued" | "running" | "done" | "error";
  reason: "manual" | "connect" | "scheduled" | "compliance_check";
  sources: EvidenceSourceKey[];
  imported: number;
  updated: number;
  tagged: number;
  skipped: number;
  errorMessage: string | null;
  detailsJson: Array<{ fileName: string; source: EvidenceSourceKey; outcome: "imported" | "updated" | "skipped" | "failed"; reason?: string; tags?: number; policyAdopted?: boolean; documentId?: string }> | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

interface ImportStatus {
  sources: EvidenceSourceStatus[];
  activeRun: ImportRun | null;
  latestRun: ImportRun | null;
  /** The quality review of each file the last run filed, by document id (backend lib/evidenceQuality.ts). */
  reviews?: Record<string, QualityReview>;
}

export const SOURCE_LABELS: Record<EvidenceSourceKey, string> = {
  m365: "Microsoft 365",
  onedrive: "OneDrive",
  sharepoint: "SharePoint",
  google_drive: "Google Drive",
};

const REASON_LABELS: Record<ImportRun["reason"], string> = {
  manual: "started by hand",
  connect: "after connecting",
  scheduled: "scheduled",
  compliance_check: "before a compliance check",
};

// The platform's one date-time format (lib/format.ts).
const fmtWhen = (iso: string): string => fmtDateTime(iso);

export function runSummary(run: ImportRun): string {
  const parts = [`${run.imported} imported`, `${run.updated} updated`, `${run.tagged} tagged`, `${run.skipped} skipped`];
  return parts.join(", ");
}

/**
 * `compact` fits the panel into a connection page's connected state (one
 * source, no source list); the full panel on the Documents page shows every
 * source and what it reads from.
 * `sourceFilter` limits the source list and the wording to one connection.
 * `onImported` fires when a run that imported or updated documents completes,
 * so the host page can reload its document list.
 */
export function EvidenceImportPanel({
  compact = false,
  sourceFilter,
  onImported,
  canImport = true,
}: {
  compact?: boolean;
  sourceFilter?: EvidenceSourceKey[];
  onImported?: () => void;
  canImport?: boolean;
}) {
  const [status, setStatus] = useState<ImportStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const pollRef = useRef<number | null>(null);
  const notifiedRunRef = useRef<string | null>(null);
  // Host pages pass a fresh callback every render; keep the latest without restarting the poll.
  const onImportedRef = useRef(onImported);
  useEffect(() => { onImportedRef.current = onImported; }, [onImported]);

  const load = useCallback(async () => {
    try {
      const data = await api.get<ImportStatus>("/documents/import-status");
      setStatus(data);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load import status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while a run is queued or running; when it finishes, refresh the
  // status and let the host page know if anything changed.
  useEffect(() => {
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
    const active = status?.activeRun;
    if (!active) return;
    pollRef.current = window.setInterval(async () => {
      try {
        const { run } = await api.get<{ run: ImportRun }>(`/documents/import-runs/${active.id}`);
        if (run.status === "done" || run.status === "error") {
          if (notifiedRunRef.current !== run.id && run.imported + run.updated > 0) {
            notifiedRunRef.current = run.id;
            onImportedRef.current?.();
          }
          await load();
        }
      } catch { /* keep polling; the next status load reports the error */ }
    }, 3000);
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
  }, [status?.activeRun, load]);

  async function startImport() {
    setStarting(true);
    setError("");
    try {
      await api.post("/documents/import-connected", {});
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not start the import.");
    } finally {
      setStarting(false);
    }
  }

  if (loading) return null;
  if (!status) return error ? <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div> : null;

  const sources = sourceFilter ? status.sources.filter((s) => sourceFilter.includes(s.key)) : status.sources;
  const connected = sources.filter((s) => s.connected);
  if (connected.length === 0) return null; // nothing to import from — the page's connect UI already says so

  const importable = sources.some((s) => s.importable);
  const active = status.activeRun;
  const latest = status.latestRun;
  const details = latest?.detailsJson ?? [];
  const interesting = details.filter((d) => d.outcome !== "skipped" || (d.reason && !/Unchanged|Already in the repository/.test(d.reason)));

  return (
    <div style={{ ...card, padding: compact ? "14px 16px" : "16px 20px", marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CloudDownload size={16} color={theme.color.accentText} />
            <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>
              {compact ? "Evidence import" : "Import evidence from connected storage"}
            </span>
          </div>
          <div style={{ fontSize: 12, color: theme.color.muted, marginTop: 4, lineHeight: 1.5 }}>
            New and changed files are copied into the Evidence Repository and tagged to the controls they support.
            This also runs after connecting, every six hours, and before each compliance check.
          </div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 4, lineHeight: 1.5 }}>
            Your Policies folders are read too: a policy still exactly as the platform generated it is left alone as a
            draft, and once you have edited it there it is taken in as your organisation's own adopted policy.
          </div>
          {!compact && (
            <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
              {sources.map((s) => (
                <div key={s.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12 }}>
                  {s.importable
                    ? <CircleCheck size={14} color={theme.color.successText} style={{ marginTop: 2, flexShrink: 0 }} />
                    : <CircleAlert size={14} color={s.connected ? theme.color.warningText : theme.color.faint} style={{ marginTop: 2, flexShrink: 0 }} />}
                  <span style={{ color: theme.color.text }}>
                    <strong>{s.label}</strong>
                    <span style={{ color: theme.color.muted }}> — {s.note}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          {compact && sources.map((s) => (
            <div key={s.key} style={{ fontSize: 12, color: s.importable ? theme.color.muted : theme.color.warningText, marginTop: 6 }}>
              {s.note}
            </div>
          ))}
        </div>

        {canImport && (
          <button
            onClick={startImport}
            disabled={starting || !!active || !importable}
            style={{ ...(active ? btnSecondary : btnPrimary), display: "flex", alignItems: "center", gap: 6, opacity: starting || !!active || !importable ? 0.65 : 1, whiteSpace: "nowrap" }}
          >
            <RefreshCw size={14} style={active ? { animation: "spin 1s linear infinite" } : undefined} />
            {active ? (active.status === "running" ? "Importing…" : "Queued…") : starting ? "Starting…" : "Import now"}
          </button>
        )}
      </div>

      {error && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 10 }}>{error}</div>}

      {latest && !active && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${theme.color.border}`, fontSize: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {latest.status === "error"
              ? <CircleAlert size={14} color={theme.color.dangerText} />
              : <CircleCheck size={14} color={theme.color.successText} />}
            <span style={{ color: theme.color.text }}>
              Last import {latest.completedAt ? fmtWhen(latest.completedAt) : fmtWhen(latest.createdAt)} ({REASON_LABELS[latest.reason]}): {runSummary(latest)}
            </span>
            {interesting.length > 0 && (
              <button
                onClick={() => setShowDetails((v) => !v)}
                style={{ background: "none", border: "none", color: theme.color.accentText, fontSize: 12, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 2 }}
              >
                {showDetails ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                {showDetails ? "Hide files" : `${interesting.length} file${interesting.length === 1 ? "" : "s"}`}
              </button>
            )}
          </div>
          {latest.errorMessage && (
            <div style={{ color: latest.status === "error" ? theme.color.dangerText : theme.color.warningText, marginTop: 4 }}>{latest.errorMessage}</div>
          )}
          {showDetails && interesting.length > 0 && (
            <div style={{ marginTop: 8, display: "grid", gap: 3, maxHeight: 220, overflowY: "auto" }}>
              {interesting.map((d, i) => (
                <div key={`${d.fileName}-${i}`} style={{ display: "flex", gap: 8, color: theme.color.muted, flexWrap: "wrap" }}>
                  <StatusTag tone={d.outcome === "failed" ? "gap" : d.outcome === "skipped" ? "none" : "met"}>
                    {d.outcome}
                  </StatusTag>
                  <span style={{ color: theme.color.text, wordBreak: "break-all" }}>{d.fileName}</span>
                  {d.documentId && <QualityChip review={status?.reviews?.[d.documentId]} />}
                  <span>{SOURCE_LABELS[d.source]}{d.policyAdopted ? " · adopted as your policy" : ""}{typeof d.tags === "number" && d.outcome !== "skipped" && d.outcome !== "failed" ? ` · ${d.tags} tag${d.tags === 1 ? "" : "s"}` : ""}{d.reason ? ` · ${d.reason}` : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Documents the platform writes itself from a register — not an import source.
// Display mirror of backend/src/lib/generatedEvidence.ts, which is also what
// decides the Word document each becomes when somebody downloads it.
const GENERATED_SOURCE_LABELS: Record<string, string> = {
  dora_register: "DORA register",
  resilience_register: "Resilience register",
  risk_register: "Risk register",
  tprm_register: "Supplier register",
  dp_register: "Data protection register",
  threat_register: "Threat advisory triage",
  improvement_register: "Corrective action register",
  ai_register: "AI system register",
  physical_register: "Physical security records",
  submission_register: "Regulatory submissions",
  governance_register: "Policy approvals and acknowledgements",
  bia_register: "Business impact analyses",
  vulnerability_register: "Vulnerability management",
  exposure_register: "Quantified risk exposure",
  remediation_register: "Remediation automation log",
  people_register: "Joiners, movers and leavers",
  discovery_register: "Sensitive data map",
  hardening_register: "CIS hardening",
  estate_register: "Network segmentation and firewalls",
  connector_posture: "Connected security sources",
  management_system_register: "Management system records",
  scope_register: "Technical scopes",
  access_review_register: "Access reviews",
  policy_exception_register: "Policy exceptions",
  change_register: "Change register",
  device_register: "Linked devices",
};

/** Small badge naming where an imported document came from, with a link back to it when the source gave one. */
export function EvidenceSourceBadge({ source, webUrl }: { source?: string | null; webUrl?: string | null }) {
  if (!source || source === "upload") return null;
  const label = SOURCE_LABELS[source as EvidenceSourceKey] ?? GENERATED_SOURCE_LABELS[source] ?? source;
  const style: React.CSSProperties = {
    fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 3,
    background: theme.color.panelAlt, color: theme.color.muted, border: `1px solid ${theme.color.border}`,
    textDecoration: "none", whiteSpace: "nowrap",
  };
  const href = webUrl && /^https?:\/\//i.test(webUrl.trim()) ? webUrl.trim() : undefined;
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" style={style} title={`Open in ${label}`}>{label}</a>
    : <span style={style}>{label}</span>;
}
